import React, { useMemo } from "react";
import { EmptyReport, ReportPanel } from "./ReportCards";
import { n } from "./reportFormat";

const AXIS_TICKS = 5;
const MAX_X_LABELS = 7;

function buildPath(points) {
  if (!points.length) return "";
  return points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" ");
}

function finiteNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function compactNumber(value) {
  const parsed = finiteNumber(value);
  return new Intl.NumberFormat("en-IN", {
    notation: Math.abs(parsed) >= 1000 ? "compact" : "standard",
    maximumFractionDigits: Math.abs(parsed) >= 1000 ? 1 : 0,
  }).format(parsed);
}

function getRowKey(row) {
  return row?.bucket || row?.date || row?.created_at || row?.last_checked || row?.last_request_at || row?.name || "";
}

function formatDateLike(value, withTime = false) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-IN", withTime
    ? { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }
    : { day: "2-digit", month: "short" });
}

function xLabel(row, allRows) {
  if (row?.name) return String(row.name);

  const raw = getRowKey(row);
  const parsedDates = allRows
    .map((item) => new Date(getRowKey(item)))
    .filter((date) => !Number.isNaN(date.getTime()));

  const spanMs = parsedDates.length > 1
    ? Math.max(...parsedDates.map((d) => d.getTime())) - Math.min(...parsedDates.map((d) => d.getTime()))
    : 0;

  return formatDateLike(raw, spanMs > 0 && spanMs <= 2 * 86400000);
}

function tooltipLabel(row) {
  if (row?.name) return String(row.name);
  return formatDateLike(getRowKey(row), true);
}

function selectedTickIndexes(count, maxTicks = MAX_X_LABELS) {
  if (count <= 0) return [];
  if (count <= maxTicks) return Array.from({ length: count }, (_, i) => i);

  const indexes = new Set([0, count - 1]);
  for (let i = 1; i < maxTicks - 1; i += 1) {
    indexes.add(Math.round((i * (count - 1)) / (maxTicks - 1)));
  }
  return [...indexes].sort((a, b) => a - b);
}

function paddedDomain(values) {
  const rawMin = Math.min(...values, 0);
  const rawMax = Math.max(...values, 0);

  if (rawMin === rawMax) {
    const extra = Math.max(Math.abs(rawMax) * 0.15, 1);
    return { min: rawMin - extra, max: rawMax + extra };
  }

  const spread = rawMax - rawMin;
  const pad = spread * 0.1;
  let min = rawMin < 0 ? rawMin - pad : 0;
  let max = rawMax > 0 ? rawMax + pad : 0;

  if (max - min < 1) max = min + 1;
  return { min, max };
}

export default function TrendBars({ rows = [], field = "revenue", label = "Trend", subtitle = "These filters are only for this trend API", height = 300 }) {
  const chart = useMemo(() => {
    const safeRows = Array.isArray(rows) ? rows.filter(Boolean) : [];
    const values = safeRows.map((row) => finiteNumber(row?.[field]));
    const { min, max } = paddedDomain(values.length ? values : [0]);
    const width = 1000;
    const svgHeight = Math.max(height, 280);
    const pad = { top: 22, right: 22, bottom: 52, left: 62 };
    const innerW = width - pad.left - pad.right;
    const innerH = svgHeight - pad.top - pad.bottom;
    const denom = Math.max(max - min, 1);

    const points = safeRows.map((row, index) => {
      const value = finiteNumber(row?.[field]);
      return {
        row,
        value,
        x: pad.left + (safeRows.length <= 1 ? innerW / 2 : (index / (safeRows.length - 1)) * innerW),
        y: pad.top + innerH - ((value - min) / denom) * innerH,
      };
    });

    const line = buildPath(points);
    const baselineY = pad.top + innerH - ((0 - min) / denom) * innerH;
    const areaBase = Math.min(pad.top + innerH, Math.max(pad.top, baselineY));
    const area = points.length
      ? `${line} L${points[points.length - 1].x.toFixed(2)},${areaBase.toFixed(2)} L${points[0].x.toFixed(2)},${areaBase.toFixed(2)} Z`
      : "";

    const grid = Array.from({ length: AXIS_TICKS }, (_, index) => {
      const ratio = index / (AXIS_TICKS - 1);
      const value = max - (max - min) * ratio;
      return { y: pad.top + innerH * ratio, value };
    });

    const tickIndexes = selectedTickIndexes(safeRows.length);
    const peakIndex = values.length ? values.indexOf(Math.max(...values)) : -1;

    return {
      width,
      height: svgHeight,
      pad,
      innerH,
      points,
      line,
      area,
      grid,
      tickIndexes,
      peakIndex,
      baselineY,
      min,
      max,
      rows: safeRows,
    };
  }, [rows, field, height]);

  if (!chart.rows.length) return <EmptyReport text={`No ${label.toLowerCase()} data for this filter`} />;

  const showEveryMarker = chart.points.length <= 32;
  const enableEveryPointHover = chart.points.length <= 180;

  return (
    <ReportPanel
      title={label}
      subtitle={subtitle}
      right={(
        <div className="flex items-center gap-2">
          <span className="rounded-full border border-[#f4cdd2] bg-[#fff8f9] px-2.5 py-1 text-[10px] font-black text-[#d86a78]">
            {n(chart.points.length)} points
          </span>
          <span className="rounded-full bg-[#f7d7dc] px-2.5 py-1 text-[10px] font-black text-[#c85364]">Trend</span>
        </div>
      )}
    >
      <div className="relative w-full overflow-x-auto overflow-y-hidden rounded-[14px] bg-gradient-to-b from-[#fffafb] to-white px-1 pt-1">
        <svg
          viewBox={`0 0 ${chart.width} ${chart.height}`}
          className="block h-auto min-w-[660px] w-full"
          role="img"
          aria-label={`${label} chart`}
        >
          <defs>
            <linearGradient id={`trendFill-${field}`} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#d86a78" stopOpacity="0.28" />
              <stop offset="55%" stopColor="#e98c99" stopOpacity="0.11" />
              <stop offset="100%" stopColor="#d86a78" stopOpacity="0.01" />
            </linearGradient>
            <filter id={`trendGlow-${field}`} x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#d86a78" floodOpacity="0.18" />
            </filter>
          </defs>

          {chart.grid.map((gridLine, index) => (
            <g key={index}>
              <line
                x1={chart.pad.left}
                x2={chart.width - chart.pad.right}
                y1={gridLine.y}
                y2={gridLine.y}
                stroke="#f1e6e8"
                strokeWidth="1"
                strokeDasharray={index === chart.grid.length - 1 ? "0" : "4 5"}
              />
              <text x={chart.pad.left - 12} y={gridLine.y + 4} textAnchor="end" fontSize="10" fontWeight="700" fill="#94a3b8">
                {compactNumber(gridLine.value)}
              </text>
            </g>
          ))}

          {chart.min < 0 && chart.max > 0 ? (
            <line
              x1={chart.pad.left}
              x2={chart.width - chart.pad.right}
              y1={chart.baselineY}
              y2={chart.baselineY}
              stroke="#cbd5e1"
              strokeWidth="1.2"
            />
          ) : null}

          <path d={chart.area} fill={`url(#trendFill-${field})`} />
          <path
            d={chart.line}
            fill="none"
            stroke="#d86a78"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter={`url(#trendGlow-${field})`}
          />

          {chart.points.map((point, index) => {
            const visible = showEveryMarker || chart.tickIndexes.includes(index) || index === chart.peakIndex;
            if (!enableEveryPointHover && !visible) return null;
            return (
              <g key={`${getRowKey(point.row)}-${index}`}>
                <circle cx={point.x} cy={point.y} r="9" fill="transparent">
                  <title>{`${tooltipLabel(point.row)} • ${n(point.value)}`}</title>
                </circle>
                {visible ? (
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r={index === chart.peakIndex ? 4.8 : 3.5}
                    fill="#d86a78"
                    stroke="#fff"
                    strokeWidth="2"
                  >
                    <title>{`${tooltipLabel(point.row)} • ${n(point.value)}`}</title>
                  </circle>
                ) : null}
              </g>
            );
          })}

          {chart.tickIndexes.map((index) => {
            const point = chart.points[index];
            const labelText = xLabel(point.row, chart.rows);
            return (
              <g key={`tick-${index}`}>
                <line
                  x1={point.x}
                  x2={point.x}
                  y1={chart.height - chart.pad.bottom + 3}
                  y2={chart.height - chart.pad.bottom + 8}
                  stroke="#d8dee8"
                  strokeWidth="1"
                />
                <text
                  x={point.x}
                  y={chart.height - 18}
                  textAnchor={index === 0 ? "start" : index === chart.points.length - 1 ? "end" : "middle"}
                  fontSize="10"
                  fontWeight="700"
                  fill="#94a3b8"
                >
                  {String(labelText).length > 14 ? `${String(labelText).slice(0, 13)}…` : labelText}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </ReportPanel>
  );
}
